import { DefaultButton } from "common/components/Button/new/DefaultButton";
import {
  Column,
  DefaultLayout,
  Row,
} from "common/components/Layout/new/constLayout";
import {
  closeGlobalModal,
  openGlobalModal,
} from "common/utils/modalController";
import { useMemo, useState } from "react";
import { formatDateTime, getMonthRange } from "common/utils/dateUtil";
import { AreaChart } from "pages/Schedule/Data/AreaChart";
import { DataCard, DataCardLabel } from "pages/Schedule/Data/DataCard";
import { PieChart } from "pages/Schedule/Data/PieChart";
import { BarChart } from "pages/Schedule/Data/BarChart";
import { DateNavigator, TextBody1, TextHeading1 } from "common/components";
import { shortButtonStyle } from "common/const/buttonStyles";
import {
  DATE_PICKER_MODE,
  DatePicker,
} from "common/components/Etc/calendar/DatePicker";
import { colors } from "common/const/colors";
import { I18N_KEYS } from "common/i18n/keys";
import { useTranslation } from "react-i18next";
import { useSalesChartData } from "common/hooks/useSalesChartData";
import { useReservationChartData } from "common/hooks/useReservationChartData";

export const SalesChartScreen = () => {
  const { t } = useTranslation();
  const clubId = sessionStorage.getItem("clubId");
  const [targetDate, setTargetDate] = useState(new Date());
  const { firstDay, lastDay } = useMemo(
    () => getMonthRange(targetDate),
    [targetDate],
  );

  const startDate = useMemo(
    () => formatDateTime(firstDay, "YYYY-MM-DD"),
    [firstDay],
  );
  const endDate = useMemo(
    () => formatDateTime(lastDay, "YYYY-MM-DD"),
    [lastDay],
  );

  const {
    currency,
    totalAmount,
    transactionCount,
    averageAmount,
    revenueShare,
    onOffRate,
    paymentMethod,
    barChartData,
    areaChartData,
    refetchChart,
  } = useSalesChartData(startDate, endDate);
  const { summaryData } = useReservationChartData(startDate, endDate);
  const reservationRate =
    summaryData.MaxReservations > 0
      ? ((summaryData.Players / summaryData.MaxReservations) * 100).toFixed(2)
      : "0.00";

  return (
    <DefaultLayout title={"Sales analytics"}>
      <Row style={{ alignItems: "flex-end", justifyContent: "space-between" }}>
        <DateNavigator
          date={targetDate}
          format={"YYYY.MM."}
          onClick={() => {
            openGlobalModal({
              content: (
                <DatePicker
                  date={targetDate}
                  initialMode={DATE_PICKER_MODE.MONTH}
                  onSelect={(selected) => {
                    setTargetDate(selected);
                    closeGlobalModal();
                    refetchChart(selected);
                  }}
                  fixedMode
                />
              ),
            });
          }}
          onNext={() => {
            const next = new Date(targetDate);
            next.setMonth(next.getMonth() + 1);
            setTargetDate(next);
            refetchChart(next);
          }}
          onPrev={() => {
            const prev = new Date(targetDate);
            prev.setMonth(prev.getMonth() - 1);
            setTargetDate(prev);
            refetchChart(prev);
          }}
        />
        <DefaultButton.White
          style={{ ...shortButtonStyle, height: "100%" }}
          onClick={() => setTargetDate(new Date())}>
          {t(I18N_KEYS.sales.analytics.current)}
        </DefaultButton.White>
      </Row>
      <Column>
        <Row>
          <DataCard title={t(I18N_KEYS.sales.analytics.revenueMonth)}>
            <DataCardLabel>
              <TextBody1 color={colors.MainBlue} style={{ textAlign: "right" }}>
                {currency}
              </TextBody1>
              <TextHeading1
                color={colors.MainBlue}
                style={{ textAlign: "right" }}>
                {totalAmount}
              </TextHeading1>
            </DataCardLabel>
          </DataCard>
          <DataCard title={t(I18N_KEYS.sales.analytics.transactions)}>
            <TextHeading1
              color={colors.MainBlue}
              style={{ textAlign: "right" }}>
              {transactionCount}
            </TextHeading1>
          </DataCard>
        </Row>
        <Row>
          <DataCard title={t(I18N_KEYS.sales.analytics.averageAmount)}>
            <DataCardLabel>
              <TextBody1 color={colors.MainBlue} style={{ textAlign: "right" }}>
                {currency}
              </TextBody1>
              <TextHeading1
                color={colors.MainBlue}
                style={{ textAlign: "right" }}>
                {averageAmount}
              </TextHeading1>
            </DataCardLabel>
          </DataCard>
          <DataCard title={t(I18N_KEYS.sales.analytics.reservationRate)}>
            <DataCardLabel>
              <TextHeading1
                color={colors.MainBlue}
                style={{ textAlign: "right" }}>
                {reservationRate}
              </TextHeading1>
              <TextBody1 color={colors.MainBlue} style={{ textAlign: "right" }}>
                {"%"}
              </TextBody1>
            </DataCardLabel>
          </DataCard>
        </Row>
        <Row>
          <DataCard
            title={`${t(I18N_KEYS.sales.analytics.revenueShare)} (${currency})`}>
            <Row style={{ minHeight: "17.5rem", height: "100%" }}>
              <PieChart data={revenueShare} showLegend />
            </Row>
          </DataCard>
          <DataCard title={t(I18N_KEYS.sales.analytics.onOffRate)}>
            <Row style={{ minHeight: "17.5rem", height: "100%" }}>
              <PieChart data={onOffRate} showLegend />
            </Row>
          </DataCard>
        </Row>
        <Row>
          <DataCard title={t(I18N_KEYS.sales.analytics.paymentMethod)}>
            <Row style={{ minHeight: "17.5rem", height: "100%" }}>
              <PieChart data={paymentMethod} showLegend />
            </Row>
          </DataCard>
          <DataCard
            title={`${t(I18N_KEYS.sales.analytics.byWeekday)} (${currency})`}>
            <BarChart data={barChartData} />
          </DataCard>
        </Row>
        <DataCard
          title={`${t(I18N_KEYS.sales.analytics.byDate)} (${currency})`}>
          <Row style={{ minHeight: "17.5rem", height: "100%" }}>
            <AreaChart data={areaChartData} />
          </Row>
        </DataCard>
      </Column>
    </DefaultLayout>
  );
};
